import { useEffect, useState, useCallback } from "react";
import { Navbar } from "../../components";
import useViewport from "../../hooks/useViewport";
import MarketingCampaignsNavbar from "../../components/MarketingCampaignsNavbar/MarketingCampaignsNavbar";
import {
  getContactsByEmailNotifications,
  getAllContactIdsFiltered,
} from "../../api/contacts.api";
import { consultantNamesAndIds } from "../../api/consultants.api";
import { getAllMarketingCampaigns } from "../../api/marketingCampaigns.api";
import useWindowSize from "../../hooks/useWindowSize";
import { getAllTags } from "../../api/tags.api";
import { FilterMatchMode } from "primereact/api";
import MarketingCampaignSendHeader from "./MarkCampaignSend-Components/MarketingCampaignSendHeader";
import MarketingsCampaignTable from "./MarkCampaignSend-Components/MarketingCampaignTable";
import MarketingCampaignSendFiltersHeader from "./MarkCampaignSend-Components/MarketingCampaignSendFiltersHeader";
import debounce from "lodash.debounce";

import {
  organizeCostaZones,
  organizeZonesByGroup,
  PATRIMONIAL_GROUPS,
  RESIDENTIAL_GROUPS,
} from "../../functions/ads/ad-functions";
import {
  residentialZones as resPromise, // Renombramos para evitar conflictos
  patrimonialZones as patPromise,
  costaZones as costaPromise,
} from "../../constants/ads-requests-constants";

import "./MarketingCampaignsSend.scss";

const MarketingCampaignsSend = ({ subTitle }) => {
  useViewport();

  const [marketingsCampaigns, setMarketingsCampaigns] = useState([]);
  const [selectedMarketingCampaign, setSelectedMarketingCampaign] = useState(
    [],
  );
  const [contacts, setContacts] = useState([]);
  const [contactsToSend, setContactsToSend] = useState([]);
  const [openSendMarketingCampaignEmail, setOpenSendMarketingCampaignEmail] =
    useState(false);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [consultants, setConsultants] = useState([]);

  // estados para paginación
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
  });
  const [allZones, setAllZones] = useState([]);

  const size = useWindowSize();

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    tags: { value: null, matchMode: FilterMatchMode.IN, operator: "or" },
    contactType: { value: null, matchMode: FilterMatchMode.IN, operator: "or" },
    consultant: { value: null, matchMode: FilterMatchMode.IN },
    adZones: { value: null, matchMode: FilterMatchMode.IN }, // Zonas Propietario
    reqZones: { value: null, matchMode: FilterMatchMode.IN }, // Zonas Cliente
  });

  const fetchGetContactsByEmailNotifications = async (
    updatedTagIds = null,
    updatedTypes = null,
    updatedOperator = null,
    updatedGlobalSearch = null,
    updatedPage = null,
    updatedRows = null,
    updatedTagOperator = null,
    updatedConsultants = null,
    updatedAdZones = null,
    updatedReqZones = null,
  ) => {
    setLoading(true);
    try {
      const isFilterChange = updatedPage === null;
      const page = isFilterChange ? 1 : updatedPage;
      const rows = updatedRows !== null ? updatedRows : lazyParams.rows;

      // 1. Mapeo independiente de AdZones
      const adZoneIds =
        updatedAdZones !== null
          ? updatedAdZones
          : filters.adZones.value?.map((z) => z._id) || [];

      // 2. Mapeo independiente de ReqZones
      const reqZoneIds =
        updatedReqZones !== null
          ? updatedReqZones
          : filters.reqZones.value?.map((z) => z._id) || [];

      const tagIds =
        updatedTagIds !== null
          ? updatedTagIds
          : filters.tags.value?.map((t) => t._id) || [];

      const consultantIds =
        updatedConsultants !== null
          ? updatedConsultants
          : filters.consultant.value?.map((c) => c._id) || [];

      const contactTypes =
        updatedTypes !== null ? updatedTypes : filters.contactType.value || [];

      const typeOperator =
        updatedOperator !== null
          ? updatedOperator
          : filters.contactType.operator || "or";

      const tagOperator =
        updatedTagOperator !== null
          ? updatedTagOperator
          : filters.tags.operator || "or";

      const globalSearch =
        updatedGlobalSearch !== null
          ? updatedGlobalSearch
          : filters.global.value || "";

      const res = await getContactsByEmailNotifications(
        tagIds,
        contactTypes,
        typeOperator,
        globalSearch,
        page,
        rows,
        tagOperator,
        consultantIds,
        adZoneIds, // Parámetro 9: adZones
        reqZoneIds, // Parámetro 10: reqZones
      );

      setContacts(res.contacts);
      setTotalRecords(res.totalRecords);

      // Verificamos si es un cambio de filtro real (no paginación)
      // o si se han pasado valores específicos de consultores o tipos
      /* const hasFilterChanged =
        updatedConsultants !== null ||
        updatedOperator !== null ||
        updatedTagOperator !== null;

      if (hasFilterChanged) {
        setContactsToSend([]); // Solo limpiamos si el usuario cambió el "qué" busca, no el "dónde" está (página)
        setLazyParams({ first: 0, rows: rows, page: 1 });
      } */

      if (isFilterChange) {
        setLazyParams({ first: 0, rows: rows, page: 1 });
      }
    } catch (error) {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  // Modificamos el evento de paginación
  const onPage = (event) => {
    setLazyParams(event);
    const newPage = event.first / event.rows + 1;
    fetchGetContactsByEmailNotifications(
      null,
      null,
      null,
      null,
      newPage,
      event.rows,
    );
  };

  async function fetchgetAllMarketingCampaigns() {
    const campaigns = await getAllMarketingCampaigns();
    setMarketingsCampaigns(campaigns);
  }

  async function fetchAllTags() {
    const res = await getAllTags();
    setTags(res.tags);
  }

  async function fetchAllConsultants() {
    const res = await consultantNamesAndIds();
    setConsultants(res.consultants || res);
  }

  const fetchZones = async () => {
    try {
      // Cargamos todas las zonas igual que hicimos en AdsTable
      const [res, pat, costa] = await Promise.all([
        resPromise,
        patPromise,
        costaPromise,
      ]);

      // Organizamos usando tus funciones originales (esto añade .zone a cada objeto)
      const organizedRes = organizeZonesByGroup(res, RESIDENTIAL_GROUPS);
      const organizedPat = organizeZonesByGroup(pat, PATRIMONIAL_GROUPS);
      const organizedCosta = organizeCostaZones(costa);

      // Función interna para crear la estructura de PrimeReact: { label, items }
      const formatForPrime = (data, mainLabel) => {
        const subGroups = {};
        data.forEach((item) => {
          const subGroupName = item.zone || "Otros";
          const groupKey = `${mainLabel} - ${subGroupName}`;

          if (!subGroups[groupKey]) {
            subGroups[groupKey] = {
              label: groupKey.toUpperCase(),
              items: [],
            };
          }
          subGroups[groupKey].items.push(item);
        });
        return Object.values(subGroups);
      };

      // Unimos todo en la lista de opciones agrupadas
      const groupedOptions = [
        ...formatForPrime(organizedRes, "RESIDENCIAL"),
        ...formatForPrime(organizedPat, "PATRIMONIO"),
        ...formatForPrime(organizedCosta, "COSTA"),
      ];

      setAllZones(groupedOptions);
    } catch (error) {
      console.error("Error al cargar zonas agrupadas:", error);
    }
  };

  useEffect(() => {
    fetchGetContactsByEmailNotifications([], []);
    fetchgetAllMarketingCampaigns();
    fetchAllTags();
    fetchAllConsultants();
    fetchZones();
  }, []);

  const handleOpenSendMarketingCampaignEmail = () => {
    setOpenSendMarketingCampaignEmail(!openSendMarketingCampaignEmail);
  };

  const debouncedFetch = useCallback(
    debounce((searchValue) => {
      fetchGetContactsByEmailNotifications(null, null, null, searchValue);
    }, 500), // Espera 500ms tras dejar de escribir
    [filters],
  );

  const handleInputFilterChange = (e) => {
    const value = e.target.value;
    setFilters((prev) => ({
      ...prev,
      global: { ...prev.global, value: value },
    }));

    // Only call backend if input is empty (for deleting search) or has 4+ chars.
    if (value.length === 0 || value.length >= 4) {
      debouncedFetch(value);
    }
  };

  const selectAllFilteredContacts = async () => {
    setLoading(true);
    try {
      const tagIds = filters.tags.value?.map((t) => t._id) || [];
      const contactTypes = filters.contactType.value || [];
      const consultantIds = filters.consultant.value?.map((c) => c._id) || [];
      const adZoneIds = filters.adZones.value?.map((z) => z._id) || [];
      const reqZoneIds = filters.reqZones.value?.map((z) => z._id) || [];

      const typeOperator = filters.contactType.operator || "or";
      const tagOperator = filters.tags.operator || "or";
      const globalSearch = filters.global.value || "";

      const allContacts = await getAllContactIdsFiltered(
        tagIds,
        contactTypes,
        typeOperator,
        globalSearch,
        tagOperator,
        consultantIds,
        adZoneIds,
        reqZoneIds,
      );

      setContactsToSend(allContacts);
    } catch (error) {
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="LayoutMarketingCampaigns">
      <Navbar title={subTitle} />
      <div className="LayoutMarketingCampaigns__contain">
        <MarketingCampaignsNavbar />

        <div className="LayoutMarketingCampaigns__contain__children">
          <MarketingCampaignSendHeader
            contactsToSend={contactsToSend}
            selectedMarketingCampaign={selectedMarketingCampaign}
            handleOpenSendMarketingCampaignEmail={
              handleOpenSendMarketingCampaignEmail
            }
          />

          {/* SELECCIÓN DE CAMPAÑA Y BUSCADOR */}

          <MarketingCampaignSendFiltersHeader
            marketingsCampaigns={marketingsCampaigns}
            setSelectedMarketingCampaign={setSelectedMarketingCampaign}
            filters={filters}
            size={size}
            handleInputFilterChange={handleInputFilterChange}
          />

          <MarketingsCampaignTable
            contacts={contacts}
            contactsToSend={contactsToSend}
            setContactsToSend={setContactsToSend}
            openSendMarketingCampaignEmail={openSendMarketingCampaignEmail}
            handleOpenSendMarketingCampaignEmail={
              handleOpenSendMarketingCampaignEmail
            }
            selectedMarketingCampaign={selectedMarketingCampaign}
            filters={filters}
            setFilters={setFilters}
            tags={tags}
            fetchGetContactsByEmailNotifications={
              fetchGetContactsByEmailNotifications
            }
            lazyParams={lazyParams}
            totalRecords={totalRecords}
            onPage={onPage}
            loading={loading}
            selectAllFilteredContacts={selectAllFilteredContacts}
            consultants={consultants}
            allZones={allZones}
          />
        </div>
      </div>
    </div>
  );
};

export default MarketingCampaignsSend;
